import { SPORT_ID, getSportConfig, getDefaultDemoPayload } from '@/config/sports'

// =============================================================================
// Sport-specific Score Types
// =============================================================================

export interface VolleyballScore {
  points?: number
  sets?: number
  isServing?: boolean
}

export interface RacquetScore {
  points?: number
  advantage?: boolean
  sets?: number
  games?: number
  isServing?: boolean
}

export interface BaseballScore {
  runs?: number
  hits?: number
  errors?: number
}

// =============================================================================
// Cricket — manual vs comp split
// =============================================================================
// Cricket has TWO independent scoring paths in this v2 renderer. They are
// deliberately separated at the TYPE and FOLDER level so a future
// comp-cricket implementation can land alongside the manual one without
// renames or churn:
//
//   Manual cricket  → operator types free-text via the FrogBox Go cricket
//                     panel (this branch: CricketManualScore /
//                     CricketManualEventState, rendered by
//                     ScoreTemplates/Cricket/Manual/CricketManualTemplate).
//   Comp cricket    → driven by a competition provider feed. Types and
//                     templates land in ScoreTemplates/Cricket/Comp/* when
//                     built (CricketCompScore / CricketCompEventState /
//                     CricketCompTemplate).
//
// Match.tsx routes purely on sportId — SPORT_ID.CRICKET_MANUAL (12) maps to
// the manual template; SPORT_ID.CRICKET (1) is reserved for comp cricket.
// When comp lands, define CricketCompScore + CricketCompEventState here,
// build the templates under Cricket/Comp/, and register SPORT_ID.CRICKET in
// templateComponents. Do NOT collapse the two paths into one type or one
// template — the manual UX (free-text strings, "YTB" placeholder, "3/120"
// verbatim) is fundamentally incompatible with structured comp data.
// =============================================================================

/**
 * Score shape for the **manual** cricket scorebug (no competition provider).
 * Both fields are free-text strings — operator types whatever convention
 * applies (3/120, 120/3, 120, 45 indoor, "YTB" placeholder, "10.2" overs
 * etc) and the renderer prints it verbatim. No parsing.
 *
 * Comp cricket gets its own CricketCompScore type (not yet defined) when
 * that path is implemented.
 */
export type CricketScoreFormat = 'WICKETS_RUNS' | 'RUNS_WICKETS' | 'RUNS_ONLY'

export interface CricketManualScore {
  score?: string
  overs?: string
  /** Structured mirrors of `score`, for consumers that recompose it. Not read
      by this template — `score` stays the rendered string. */
  runs?: number
  wkts?: number
}

/**
 * Score shape for goal-based sports (soccer, netball, hockey, ice hockey,
 * Euro handball etc). Cards are a flexible color->count dict so any sport's
 * disciplinary palette fits without schema changes (soccer: yellow/red,
 * field hockey: green/yellow/red, handball: yellow/red plus 2-min via a
 * separate field if added later).
 */
export interface SoccerScore {
  goals?: number
  cards?: Record<string, number>
}

/**
 * Tennis competitor score: completed sets (with optional tiebreak score),
 * in-progress set games, current game points. `currentGamePoints` carries
 * only the regulation lexicon ('0','15','30','40','AD'); tiebreak /
 * super-tiebreak numerics live in `eventState.currentGame.tiebreakPoints`,
 * not here.
 */
export interface TennisCompletedSet {
  games: number
  tiebreak: number | null
}

export interface TennisScore {
  sets?: TennisCompletedSet[]
  currentSetGames?: number
  currentGamePoints?: '0' | '15' | '30' | '40' | 'AD'
}

// Union type for all sport-specific scores
export type CompetitorScore =
  | VolleyballScore
  | RacquetScore
  | BaseballScore
  | SoccerScore
  | CricketManualScore
  | TennisScore

// =============================================================================
// Shared Event State Types
// =============================================================================

export type ScoreBugPosition = 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

// =============================================================================
// Sport-specific Event State Types
// =============================================================================

export interface BaseballEventState {
  inning?: number
  inningState?: 'TOP' | 'MID' | 'BOT' | 'END'
  outs?: number
  bases?: boolean[] // [1st base, 2nd base, 3rd base]
  isFinal?: boolean
}

export type GoalSportPhase = 'PRE' | 'RUNNING' | 'BREAK' | 'END'

export interface GoalSportEventState {
  period?: number
  periodCount?: number
  phase?: GoalSportPhase
  periodStartedAt?: string | null
  // Wall-clock elapsed seconds at the end of each period.
  // Index n = end of period (n+1). Slots not yet completed should be null.
  // Backend may send fewer entries than periodCount; missing entries are treated as null.
  periodEndElapsedSeconds?: (number | null)[]
}

/**
 * Cricket **manual** event state — operator-driven free-text bug. Toggles
 * drive row collapse and overs visibility; batting moves the active dot.
 * Every field is optional and defaults to the two-team, current-overs-only
 * bug, so payloads from writers that predate a field keep rendering.
 *
 * Manual/comp routing lives on `sportId` (see SPORT_ID.CRICKET_MANUAL = 12
 * vs SPORT_ID.CRICKET = 1) — see the "Cricket — manual vs comp split"
 * doc-block at the top of this file. Comp cricket will introduce a sibling
 * `CricketCompEventState` when that path is implemented.
 */
export interface CricketManualEventState {
  batting?: 1 | 2
  showTeam1?: boolean
  showTeam2?: boolean
  showOvers?: boolean
  /** Overs allotted for the match, shared by both teams. */
  availableOvers?: number
  showAvailableOvers?: boolean
  /** Recorded for consumers that recompose `score`; the order is already
      baked into the string this template prints. */
  scoreFormat?: CricketScoreFormat
}

export type TennisFormat = 'BO3' | 'PRO_SET'
export type TennisDeuceRule = 'STANDARD' | 'NO_AD'
export type TennisFinalSetRule = 'FULL_SET' | 'SUPER_TIEBREAK'
export type TennisPhase = 'PRE' | 'LIVE' | 'END'

export interface TennisCurrentGame {
  isTiebreak: boolean
  isSuperTiebreak: boolean
  // Inner home/away can be null until either side has scored a tiebreak
  // point (operator form fields are nullable until tiebreak engages).
  // Render sites must coerce with `?? 0`.
  tiebreakPoints: null | { home: number | null; away: number | null }
}

export interface TennisEventState {
  format?: TennisFormat
  isDoubles?: boolean
  deuceRule?: TennisDeuceRule
  finalSetRule?: TennisFinalSetRule
  currentSetIndex?: number
  currentGame?: TennisCurrentGame
  server?: 'home' | 'away'
  serverDoublesSlot?: 0 | 1 | 2 | 3
  phase?: TennisPhase
}

// Union type for all sport-specific event states
export type EventState =
  | BaseballEventState
  | GoalSportEventState
  | TennisEventState
  | CricketManualEventState
  | Record<string, unknown>

// =============================================================================
// Core Types
// =============================================================================

export interface Competitor {
  name?: string
  shortName?: string
  isHome?: boolean
  color?: string
  score?: CompetitorScore
}

export interface EventTheme {
  primaryColor?: string
  secondaryColor?: string
  scoreColor?: string
}

export interface DisplayStateConfig {
  element?: number | string
  position?: ScoreBugPosition
}

// Key is the slotId (see GFX_SLOT_ID in src/types). New slots need no type changes.
// Note: JSON deserialises numeric keys as strings — numeric access coerces correctly at runtime.
export type GfxState = Partial<Record<number, { visible: boolean }>>

export interface JsonMatchData {
  sportId: number
  streamGuid?: string
  eventGuid?: string
  eventTitle?: string
  eventDescription?: string
  eventStartTime?: string | null
  displayState?: DisplayStateConfig | number | string
  eventState?: EventState | null
  competitors?: Competitor[]
  theme?: EventTheme
  gfxState?: GfxState
  lastUpdated?: string
}

/**
 * Parse displayState — handles both legacy flat format (string/number) and new object format
 */
export const parseDisplayState = (jsonData: JsonMatchData | null): { element: string; position: ScoreBugPosition } => {
  const ds = jsonData?.displayState
  if (ds && typeof ds === 'object') {
    // New format: { element: "SCOREBUG" | 1, position: "bottom-left" }
    const element =
      typeof ds.element === 'number'
        ? (['INTRO', 'SCOREBUG', 'SCOREBAR'][ds.element] ?? 'INTRO')
        : String(ds.element ?? 'INTRO')
    return { element, position: ds.position ?? 'bottom-left' }
  }
  // Legacy format: flat string or number
  if (typeof ds === 'number') {
    return { element: ['INTRO', 'SCOREBUG', 'SCOREBAR'][ds] ?? 'INTRO', position: 'bottom-left' }
  }
  return { element: String(ds ?? 'INTRO'), position: 'bottom-left' }
}

/**
 * Get the home competitor from the competitors array
 */
export const getHomeCompetitor = (jsonData: JsonMatchData | null): Competitor | undefined => {
  return jsonData?.competitors?.find((c) => c.isHome)
}

/**
 * Get the away competitor from the competitors array
 */
export const getAwayCompetitor = (jsonData: JsonMatchData | null): Competitor | undefined => {
  return jsonData?.competitors?.find((c) => !c.isHome)
}

// Current demo sport for sport-specific match data
let currentDemoSport: SPORT_ID = SPORT_ID.VOLLEYBALL

/**
 * Set the current demo sport for match data fetching
 */
export const setDemoMatchDataSport = (sportId: SPORT_ID) => {
  currentDemoSport = sportId
}

/**
 * Get the match data path for the current demo sport.
 * Prefers `defaultDemoPayload` so the demo data location can diverge from the
 * template `folder` (e.g. GoalSport template lives at `goal-sport` but soccer
 * demo data lives at `/demo/soccer/`).
 */
const getDemoMatchDataPath = (): string => {
  const fromPayload = getDefaultDemoPayload(currentDemoSport)
  if (fromPayload) return fromPayload

  const sportConfig = getSportConfig(currentDemoSport)
  if (sportConfig) {
    return `/demo/${sportConfig.folder}/match-data.json`
  }
  return '/demo/volleyball/match-data.json'
}

export const fetchJsonMatchData = async (): Promise<JsonMatchData> => {
  try {
    const path = getDemoMatchDataPath()
    const response = await fetch(path)
    if (!response.ok) {
      throw new Error(`Failed to fetch JSON data: ${response.status}`)
    }
    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching JSON match data:', error)
    throw error
  }
}
